/**
 * Payment Service
 *
 * Handles payment-related API calls to the backend, including
 * creating Stripe Checkout sessions.
 */

import { APP_CONFIG } from "../../configs/config.js";

// Use centralized API configuration
const API_BASE_URL = APP_CONFIG.API_BASE_URL;

/**
 * Generic API request handler for payment endpoints
 * Ensures cookies are included for authentication (backend reads cookie)
 */
const apiRequest = async (endpoint, options = {}) => {
  const url = `${API_BASE_URL}${endpoint}`;

  const defaultOptions = {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
  };

  const config = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...(options.headers || {}),
    },
  };

  try {
    const response = await fetch(url, config);
    const contentType = response.headers.get("content-type");
    let data;
    if (contentType && contentType.includes("application/json")) {
      data = await response.json();
    } else {
      const text = await response.text();
      data = { detail: text };
    }

    if (!response.ok) {
      const errMsg = data?.detail || data?.message || "Request failed";
      throw new Error(errMsg);
    }

    return { success: true, data };
  } catch (error) {
    return { success: false, error: error.message || "Unknown error" };
  }
};

export const paymentService = {
  /**
   * Create a Stripe Checkout session.
   * @param {{ amount: number, description?: string, billing?: {
   *   full_name?: string,
   *   address_line1?: string,
   *   address_line2?: string,
   *   city?: string,
   *   country?: string,
   * }, returnUrl?: string, token?: string }} payload
   * @returns {Promise<{ success: boolean, data?: { checkout_url: string }, error?: string }>}
   */
  checkout: async ({ amount, description, billing, returnUrl, token }) => {
    const payload = {
      amount,
      description,
      ...(billing || {}),
    };

    // Add returnUrl if provided (backend will append to success_url)
    if (returnUrl) {
      payload.returnUrl = returnUrl;
    }

    return await apiRequest("/payment/checkout", {
      method: "POST",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });
  },

  /**
   * Get available coupon packages.
   * @param {string} token - Optional JWT for Authorization header
   * @returns {Promise<{ success: boolean, data?: { packages: Array }, error?: string }>}
   */
  getCouponPackages: async (token) => {
    return await apiRequest("/payment/coupons", {
      method: "GET",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  },

  /**
   * Create a Stripe Checkout session for a coupon package.
   * @param {{ coupon_package_id: string, billing?: {
   *   full_name?: string,
   *   address_line1?: string,
   *   address_line2?: string,
   *   city?: string,
   *   country?: string,
   * }, token?: string }} payload
   * @returns {Promise<{ success: boolean, data?: { checkout_url: string }, error?: string }>}
   */
  checkoutCoupon: async ({ coupon_package_id, billing, token }) => {
    const payload = {
      coupon_package_id,
      ...(billing || {}),
    };

    return await apiRequest("/payment/checkout-coupon", {
      method: "POST",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload),
    });
  },

  /**
   * Get current user's billing information.
   * @param {string} token - Optional JWT for Authorization header
   * @returns {Promise<{ success: boolean, data?: {
   *   full_name?: string,
   *   address_line1?: string,
   *   address_line2?: string,
   *   city?: string,
   *   country?: string
   * }, error?: string }>}
   */
  getMyBilling: async (token) => {
    return await apiRequest("/payment/me/billing", {
      method: "GET",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  },

  /**
   * Create or update user's billing information.
   * @param {Object} payload
   * @param {string=} payload.full_name
   * @param {string=} payload.address_line1
   * @param {string=} payload.address_line2
   * @param {string=} payload.city
   * @param {string=} payload.country
   * @param {string} token - Optional JWT for Authorization header
   * @returns {Promise<{ success: boolean, data?: any, error?: string }>}
   */
  updateMyBilling: async (payload, token) => {
    return await apiRequest("/payment/me/billing", {
      method: "PUT",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(payload || {}),
    });
  },

  /**
   * Get current user's payment receipts.
   * By default returns recent receipts; pass { all: true } to fetch all.
   * Optionally pass { limit } to control recent count.
   * @param {{ all?: boolean, limit?: number, token?: string }} options
   * @returns {Promise<{ success: boolean, data?: any, error?: string }>}
   */
  getMyReceipts: async (options = {}) => {
    const { all = false, token } = options;

    const params = new URLSearchParams();
    if (all) {
      params.set("all", "true");
    }

    const qs = params.toString();
    const endpoint = `/payment/me/receipts${qs ? `?${qs}` : ""}`;

    return await apiRequest(endpoint, {
      method: "GET",
      headers: {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    });
  },
};

export default paymentService;
